import { cn } from "@/lib/utils";

export type ProgressMeterTone = "ready" | "warning" | "info" | "neutral";

interface ProgressMeterProps {
  readyCount: number;
  totalCount: number;
  statusLabel: string;
  tone: ProgressMeterTone;
}

const TONE_BAR: Record<ProgressMeterTone, string> = {
  ready: "bg-emerald-500",
  warning: "bg-amber-500",
  info: "bg-sky-500",
  neutral: "bg-emerald-500",
};

const TONE_LABEL: Record<ProgressMeterTone, string> = {
  ready: "text-emerald-700 dark:text-emerald-300",
  warning: "text-amber-700 dark:text-amber-300",
  info: "text-sky-700 dark:text-sky-300",
  neutral: "text-muted-foreground",
};

export function ProgressMeter({ readyCount, totalCount, statusLabel, tone }: ProgressMeterProps) {
  const pct = totalCount > 0 ? Math.round((readyCount / totalCount) * 100) : 0;
  // "1 de 1 listo" → singular agrees with the singular total. Any other case
  // sees a plural total, so the adjective stays "listos".
  const readyWord = totalCount === 1 ? "listo" : "listos";
  return (
    <div className="mt-3 mb-1 space-y-1.5 px-1">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-[13px] font-medium tabular-nums">
          {readyCount}{" "}
          <span className="font-normal text-muted-foreground">de {totalCount} {readyWord}</span>
        </p>
        <p className={cn("text-[11px] font-semibold uppercase tracking-[0.08em]", TONE_LABEL[tone])}>
          {statusLabel}
        </p>
      </div>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={totalCount}
        aria-valuenow={readyCount}
        aria-label={`${readyCount} de ${totalCount} ingredientes ${readyWord}`}
        className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
      >
        <div
          className={cn("h-full rounded-full transition-[width] duration-500 ease-out", TONE_BAR[tone])}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
